import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { FormControl } from "@angular/forms";

type LogicOperator = 'all' | 'any';

export interface QueryBuilderFieldMeta {
  id: string;
  name: string;
  type: 'String' | 'Number' | 'Integer' | 'Float' | 'Boolean' | 'Date' | 'Array' | 'String[]' | 'Number[]' | 'Integer[]' | 'Float[]';
  group?: 'standard' | 'device' | 'custom' | string;
  options?: Array<{ label: string; value: any }>;
}

interface RuleNode {
  kind: 'rule';
  fieldId: string | null;
  type: QueryBuilderFieldMeta['type'] | null;
  operator: string | null;
  value?: any;
}

interface GroupNode {
  kind: 'group';
  operator: LogicOperator;
  negate: boolean;
  children: Array<GroupNode | RuleNode>;
}

@Component({
  selector: 'lhpe-query-builder',
  templateUrl: './query-builder.component.html',
  styleUrls: ['./query-builder.component.scss']
})
export class QueryBuilderComponent implements OnInit {
  @Input() columns: QueryBuilderFieldMeta[] = [];
  @Input() initialWhere: any | null = null;
  @Output() whereChange = new EventEmitter<any>();
  @Output() validChange = new EventEmitter<boolean>();

  root: GroupNode = { kind: 'group', operator: 'all', negate: false, children: [] };

  // Operator catalog per type (Phase 1 subset)
  operatorCatalog: Record<QueryBuilderFieldMeta['type'], Array<{ key: string; label: string }>> = {
    String: [
      { key: 'equals', label: 'equals' },
      { key: 'not_equals', label: 'not equals' },
      { key: 'contains', label: 'contains' },
      { key: 'not_contains', label: 'does not contain' },
      { key: 'starts_with', label: 'starts with' },
      { key: 'ends_with', label: 'ends with' },
      { key: 'in', label: 'in (comma separated)' },
      { key: 'not_in', label: 'not in (comma separated)' },
      { key: 'exists', label: 'exists' },
      { key: 'not_exists', label: 'does not exist' },
    ],
    Number: [
      { key: 'equals', label: 'equals' },
      { key: 'not_equals', label: 'not equals' },
      { key: 'gt', label: 'greater than' },
      { key: 'gte', label: 'greater than or equal' },
      { key: 'lt', label: 'less than' },
      { key: 'lte', label: 'less than or equal' },
      { key: 'between', label: 'between' },
      { key: 'in', label: 'in (comma separated)' },
      { key: 'not_in', label: 'not in (comma separated)' },
      { key: 'exists', label: 'exists' },
      { key: 'not_exists', label: 'does not exist' },
    ],
    Integer: [
      { key: 'equals', label: 'equals' },
      { key: 'not_equals', label: 'not equals' },
      { key: 'gt', label: 'greater than' },
      { key: 'gte', label: 'greater than or equal' },
      { key: 'lt', label: 'less than' },
      { key: 'lte', label: 'less than or equal' },
      { key: 'between', label: 'between' },
      { key: 'in', label: 'in (comma separated)' },
      { key: 'not_in', label: 'not in (comma separated)' },
      { key: 'exists', label: 'exists' },
      { key: 'not_exists', label: 'does not exist' },
    ],
    Float: [
      { key: 'equals', label: 'equals' },
      { key: 'not_equals', label: 'not equals' },
      { key: 'gt', label: 'greater than' },
      { key: 'gte', label: 'greater than or equal' },
      { key: 'lt', label: 'less than' },
      { key: 'lte', label: 'less than or equal' },
      { key: 'between', label: 'between' },
      { key: 'in', label: 'in (comma separated)' },
      { key: 'not_in', label: 'not in (comma separated)' },
      { key: 'exists', label: 'exists' },
      { key: 'not_exists', label: 'does not exist' },
    ],
    Boolean: [
      { key: 'is_true', label: 'is true' },
      { key: 'is_false', label: 'is false' },
      { key: 'exists', label: 'exists' },
      { key: 'not_exists', label: 'does not exist' },
    ],
    Date: [
      { key: 'before', label: 'before' },
      { key: 'after', label: 'after' },
      { key: 'on', label: 'on' },
      { key: 'between', label: 'between' },
      { key: 'exists', label: 'exists' },
      { key: 'not_exists', label: 'does not exist' },
    ],
    Array: [
      { key: 'contains', label: 'contains' },
      { key: 'contains_any', label: 'contains any (comma separated)' },
      { key: 'contains_all', label: 'contains all (comma separated)' },
      { key: 'length_eq', label: 'length = (number)' },
      { key: 'length_gt', label: 'length > (number)' },
      { key: 'length_lt', label: 'length < (number)' },
      { key: 'exists', label: 'exists' },
      { key: 'not_exists', label: 'does not exist' },
    ],
    'String[]': [
      { key: 'contains', label: 'contains' },
      { key: 'contains_any', label: 'contains any (comma separated)' },
      { key: 'contains_all', label: 'contains all (comma separated)' },
      { key: 'length_eq', label: 'length = (number)' },
      { key: 'length_gt', label: 'length > (number)' },
      { key: 'length_lt', label: 'length < (number)' },
      { key: 'exists', label: 'exists' },
      { key: 'not_exists', label: 'does not exist' },
    ],
    'Number[]': [
      { key: 'contains', label: 'contains' },
      { key: 'contains_any', label: 'contains any (comma separated)' },
      { key: 'contains_all', label: 'contains all (comma separated)' },
      { key: 'length_eq', label: 'length = (number)' },
      { key: 'length_gt', label: 'length > (number)' },
      { key: 'length_lt', label: 'length < (number)' },
      { key: 'exists', label: 'exists' },
      { key: 'not_exists', label: 'does not exist' },
    ],
    'Integer[]': [
      { key: 'contains', label: 'contains' },
      { key: 'contains_any', label: 'contains any (comma separated)' },
      { key: 'contains_all', label: 'contains all (comma separated)' },
      { key: 'length_eq', label: 'length = (number)' },
      { key: 'length_gt', label: 'length > (number)' },
      { key: 'length_lt', label: 'length < (number)' },
      { key: 'exists', label: 'exists' },
      { key: 'not_exists', label: 'does not exist' },
    ],
    'Float[]': [
      { key: 'contains', label: 'contains' },
      { key: 'contains_any', label: 'contains any (comma separated)' },
      { key: 'contains_all', label: 'contains all (comma separated)' },
      { key: 'length_eq', label: 'length = (number)' },
      { key: 'length_gt', label: 'length > (number)' },
      { key: 'length_lt', label: 'length < (number)' },
      { key: 'exists', label: 'exists' },
      { key: 'not_exists', label: 'does not exist' },
    ],
  } as any;

  getOperatorsForType(t: QueryBuilderFieldMeta['type'] | null): Array<{ key: string; label: string }> {
    if (!t) { return []; }
    return this.operatorCatalog[t] || [];
  }

  ngOnInit(): void {
    if (this.initialWhere) {
      const [tree, ok] = this.hydrateFromWhere(this.initialWhere);
      if (ok && tree) {
        this.root = tree;
      }
    }
    this.emitWhere();
  }

  addRule(group: GroupNode) {
    group.children.push({ kind: 'rule', fieldId: null, type: null, operator: null, value: null });
    this.emitWhere();
  }

  addGroup(parent: GroupNode) {
    parent.children.push({ kind: 'group', operator: 'all', negate: false, children: [] });
    this.emitWhere();
  }

  removeNode(parent: GroupNode, idx: number) {
    parent.children.splice(idx, 1);
    this.emitWhere();
  }

  onFieldChanged(rule: any) {
    const meta = this.columns.find(c => c.id === rule.fieldId);
    rule.type = (meta?.type || null) as any;
    rule.operator = null;
    rule.value = null;
    this.emitWhere();
  }

  onOperatorChanged(_rule: any) {
    // Reset value when operator changes
    _rule.value = null;
    this.emitWhere();
  }

  get standardFields(): QueryBuilderFieldMeta[] { return (this.columns || []).filter(c => c.group === 'standard'); }
  get deviceFields(): QueryBuilderFieldMeta[] { return (this.columns || []).filter(c => c.group === 'device'); }
  get customFields(): QueryBuilderFieldMeta[] { return (this.columns || []).filter(c => c.group === 'custom'); }

  getGroupMatchMode(group: GroupNode): 'all' | 'any' | 'not_all' | 'not_any' {
    if (group.negate) {
      return group.operator === 'all' ? 'not_all' : 'not_any';
    }
    return group.operator;
  }

  setGroupMatchMode(group: GroupNode, value: 'all' | 'any' | 'not_all' | 'not_any') {
    if (value === 'not_all') {
      group.operator = 'all';
      group.negate = true;
    } else if (value === 'not_any') {
      group.operator = 'any';
      group.negate = true;
    } else {
      group.operator = value as LogicOperator;
      group.negate = false;
    }
    this.emitWhere();
  }

  emitWhere() {
    const [where, valid] = this.transformToWhere(this.root);
    this.whereChange.emit(where);
    this.validChange.emit(valid);
  }

  private transformToWhere(node: GroupNode): [any, boolean] {
    // Returns [whereObject, isValid]

    const toList = (v: any): any[] => {
      if (Array.isArray(v)) return v;
      if (v == null) return [];
      return String(v)
        .split(',')
        .map(s => s.trim())
        .filter(s => s.length > 0);
    };

    const mapFieldId = (fieldId: string | null): string | null => {
      if (!fieldId) return null;
      // Standard user fields were previously expressed as appUser.*
      if (fieldId.startsWith('appUser.')) {
        return 'user.' + fieldId.replace(/^appUser\./, '');
      }
      // Device fields are already device.* or plain ids like allows_push
      if (fieldId === 'allows_push') return 'permissions.push';
      if (fieldId === 'location_permission') return 'permissions.location';
      if (fieldId === 'app_version') return 'device.app_version';
      // Pass-through for already-correct ids
      return fieldId;
    };

    const mapOp = (op: string): string => {
      if (op === 'equals') return 'eq';
      if (op === 'not_equals') return 'neq';
      return op;
    };

    const mapRule = (r: RuleNode): [any, boolean] => {
      const mappedField = mapFieldId(r.fieldId);
      if (!mappedField || !r.operator) {
        return [null, false];
      }
      if (r.operator === 'between') {
        // Expect array [a,b]
        const ok = Array.isArray(r.value) && r.value.length === 2 && r.value[0] != null && r.value[1] != null;
        return [ ok ? { field: mappedField, type: r.type, op: mapOp(r.operator), value: r.value } : null, ok ];
      }
      if (r.operator === 'exists' || r.operator === 'not_exists') {
        return [ { field: mappedField, type: r.type, op: mapOp(r.operator) }, true ];
      }
      if (r.operator === 'is_true' || r.operator === 'is_false') {
        return [ { field: mappedField, type: 'Boolean', op: 'eq', value: r.operator === 'is_true' }, true ];
      }
      if (['in','not_in','contains_any','contains_all'].includes(r.operator)) {
        const arr = toList(r.value);
        const ok = arr.length > 0;
        return [ ok ? { field: mappedField, type: r.type, op: mapOp(r.operator), value: arr } : null, ok ];
      }
      if (['length_eq','length_gt','length_lt','gte','lte','not_equals','not_contains','starts_with','ends_with','before','after','on'].includes(r.operator)) {
        const has = r.value !== undefined && r.value !== null && String(r.value).length > 0;
        return [ has ? { field: mappedField, type: r.type, op: mapOp(r.operator), value: r.value } : null, has ];
      }
      const hasValue = r.value !== undefined && r.value !== null && (r.value === 0 || r.value === false || (Array.isArray(r.value) ? r.value.length > 0 : String(r.value).length > 0));
      return [ hasValue ? { field: mappedField, type: r.type, op: mapOp(r.operator), value: r.value } : null, hasValue ];
    };

    const walk = (g: GroupNode): [any, boolean] => {
      const parts: any[] = [];
      let valid = true;
      for (const child of g.children) {
        if ((child as any).kind === 'group') {
          const [sub, subValid] = walk(child as GroupNode);
          valid = valid && subValid;
          if (sub) parts.push(sub);
        } else {
          const [leaf, leafValid] = mapRule(child as RuleNode);
          valid = valid && leafValid;
          if (leaf) parts.push(leaf);
        }
      }
      if (parts.length === 0) return [null, false];
      // Use 'all'/'any' with optional 'not'
      const junction = g.operator === 'all' ? { all: parts } : { any: parts };
      return [g.negate ? { not: junction } : junction, valid];
    };

    const [res, valid] = walk(node);
    return [res, valid];
  }

  isRule(node: any): boolean { return node && node.kind === 'rule'; }
  isGroup(node: any): boolean { return node && node.kind === 'group'; }

  private hydrateFromWhere(where: any): [GroupNode | null, boolean] {
    const unmapFieldId = (serverField: string): string => {
      if (serverField.startsWith('user.')) {
        return 'appUser.' + serverField.replace(/^user\./, '');
      }
      if (serverField === 'permissions.push') return 'allows_push';
      if (serverField === 'permissions.location') return 'location_permission';
      if (serverField === 'device.app_version') return 'app_version';
      return serverField;
    };

    const unmapOp = (op: string): string => {
      if (op === 'eq') return 'equals';
      if (op === 'neq') return 'not_equals';
      return op;
    };

    const parsePredicate = (p: any): RuleNode | null => {
      if (!p || typeof p !== 'object') return null;
      if (!p.field || !p.op) return null;
      // Reverse-map field and type as-is; field mapping not needed for UI
      return {
        kind: 'rule',
        fieldId: unmapFieldId(String(p.field)),
        type: (p.type as any) || null,
        operator: unmapOp(String(p.op)),
        value: p.value,
      };
    };

    const parseGroup = (g: any): GroupNode | null => {
      if (g == null || typeof g !== 'object') return null;
      if (g.not) {
        const inner = parseGroup(g.not);
        if (!inner) return null;
        inner.negate = !inner.negate;
        return inner;
      }
      let operator: LogicOperator | null = null;
      let items: any[] | null = null;
      if (Array.isArray(g.all)) {
        operator = 'all';
        items = g.all;
      } else if (Array.isArray(g.any)) {
        operator = 'any';
        items = g.any;
      }
      if (!operator || !items) return null;
      const children: Array<GroupNode | RuleNode> = [];
      for (const item of items) {
        if (item && (Array.isArray(item.all) || Array.isArray(item.any) || item.not)) {
          const sub = parseGroup(item);
          if (sub) children.push(sub);
        } else {
          const rule = parsePredicate(item);
          if (rule) children.push(rule);
        }
      }
      return { kind: 'group', operator, negate: false, children };
    };

    const tree = parseGroup(where);
    return [tree, !!tree];
  }
}


